import { useCallback, useEffect, useRef, useState } from 'react'

import { AppClient } from '@/core/app-builder/app/app'
import { closeGlobalVariablesClient, openGlobalVariablesClient } from '@/core/app-builder/global-variables'
import constate from '@/lib/constate'
import { EVENT_KEYS } from '@/shared/custom-event'

function emitAppLoadingEvent() {
  if (window.appMode !== 'preview') return

  window.dispatchEvent(new CustomEvent(EVENT_KEYS.APP_BUILDER_LOADING))
}

function emitAppLoadedEvent(appClient: AppClient) {
  if (window.appMode !== 'preview') return

  if (!appClient || !appClient.projectSettings) return

  const { appID, projectSettings: project } = appClient

  window.dispatchEvent(
    new CustomEvent(EVENT_KEYS.APP_BUILDER_LOADED, {
      detail: {
        id: appID,
        title: project._Name,
        url: project._Domain._CustomDomain.find((d) => d._Verified)?._DomainName || project._Domain._MakyoDomain,
        icon: project._AppIcon,
        color: project._IconBackgroundColor,
        orgId: project._Organization._id,
        status: project._Status,
        loading: false,
      },
    })
  )
}

/**
 * App Client Context for Builder V2
 *
 * @note do not add unnecessary logic here!
 */
const [AppClientProvider, useAppClientContext] = constate('AppClientContext', ({ appID }: { appID: string }) => {
  // ======================== State & Refs =============================
  const [isAppClientLoading, setIsAppClientLoading] = useState(true)
  const [isAppLoading, setIsAppLoading] = useState(false)
  const appClientRef = useRef<AppClient | undefined>()
  const unsubscribeRef = useRef<() => void | undefined>()

  // ========================== Handlers =============================

  const updateAppClient = useCallback((appClient: AppClient) => {
    appClientRef.current = appClient
    window.appID = appClient.appID
    window.appUserTableID = appClient.appUserTableID || ''
  }, [])

  const closeAppClient = useCallback(() => {
    if (appClientRef.current) {
      appClientRef.current.Close()
      appClientRef.current = undefined
    }
    if (unsubscribeRef.current) {
      unsubscribeRef.current()
      unsubscribeRef.current = undefined
    }
    emitAppLoadingEvent()
    window.appID = ''
    window.appUserTableID = ''

    // close global variables manager
    closeGlobalVariablesClient()
  }, [])

  const openAppClient = useCallback(
    async (appID: string) => {
      setIsAppClientLoading(true)

      if (!!appClientRef.current) {
        closeAppClient()
      }

      emitAppLoadingEvent()

      try {
        const appClient = await AppClient.Open(appID)

        updateAppClient(appClient)

        unsubscribeRef.current = appClient.subscribe(() => {
          updateAppClient(appClient)
        })

        // intialize global variables manager
        openGlobalVariablesClient(appID)

        // inject app project for toolbar
        emitAppLoadedEvent(appClient)
      } catch (err) {
        console.error('Error loading AppClient: ', err)
      } finally {
        setIsAppClientLoading(false)
      }
    },
    [closeAppClient, updateAppClient]
  )

  // ========================== Effects =============================

  // Initial database app client when appID changes
  useEffect(() => {
    if (!appID) return

    // open app client
    openAppClient(appID)

    // listen to force-reload event
    const handleAppClientReload = () => openAppClient(appID)
    window.addEventListener(EVENT_KEYS.APP_CLIENT_RELOAD, handleAppClientReload)

    // cleanupz
    return () => {
      closeAppClient()
      window.removeEventListener(EVENT_KEYS.APP_CLIENT_RELOAD, handleAppClientReload)
    }
  }, [appID, closeAppClient, openAppClient])

  return {
    appID,
    appClient: appClientRef.current,
    isAppClientLoading,
    isAppLoading,
    setIsAppLoading,
  }
})

export { AppClientProvider, useAppClientContext }
